/**
 * IssuesSegmentBar — Arbeitspaket-Leiste auf der Arbeitsliste (P2b/B8).
 * Chips als role="group" + aria-pressed (Filter-Pills-Konvention, siehe
 * IssuesFacetSidebar/ProductSegmentBar), kein tablist. Zaehlt live via
 * evaluateSegment auf `rows` (channel-aware, siehe segmentEvaluator).
 * selectedId/customSegments/select/addSegment kommen von useIssuesSegment
 * (EINE useProductSegments-Instanz, siehe dort) statt eines eigenen Hook-Calls.
 */
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChannels } from '@/hooks/useChannels'
import { evaluateSegment } from '@/components/v2/products/segments/segmentEvaluator'
import { DEFAULT_SEGMENT_COLOR } from '@/components/v2/products/segments/segmentPresets'
import type { CustomSegment, ProductSegmentRule } from '@/components/v2/products/segments/segmentPresets'
import { SegmentChip } from '@/components/v2/products/segments/SegmentChip'
import { SegmentRuleBuilder } from '@/components/v2/products/segments/SegmentRuleBuilder'
import { Button } from '@/components/v2/ui/Button'
import type { ProductWithIssues } from '@/lib/api/channel-readiness'

interface Props {
  rows: ProductWithIssues[]
  selectedId: string
  customSegments: CustomSegment[]
  select: (id: string) => void
  addSegment: (seg: Omit<CustomSegment, 'id' | 'pinned'>) => void
}

export function IssuesSegmentBar({ rows, selectedId, customSegments, select, addSegment }: Props) {
  const { t } = useTranslation()
  const { data: channels = [] } = useChannels()
  const [builderOpen, setBuilderOpen] = useState(false)

  const countFor = useCallback(
    (rule: ProductSegmentRule) => evaluateSegment(rule, rows, channels).length,
    [rows, channels],
  )

  function handleBuilderSave(name: string, rule: ProductSegmentRule) {
    addSegment({ name, color: DEFAULT_SEGMENT_COLOR, rule })
    setBuilderOpen(false)
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label={t('productsIssues.segmentBar.groupLabel')} className="flex flex-wrap gap-1">
        <SegmentChip
          label={t('productsIssues.segmentBar.all')}
          active={selectedId === 'all'}
          onSelect={() => select('all')}
        />
        {customSegments.map((seg) => (
          <SegmentChip
            key={seg.id}
            label={`${seg.name} · ${countFor(seg.rule)}`}
            color={seg.color}
            active={selectedId === seg.id}
            onSelect={() => select(seg.id)}
          />
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={() => setBuilderOpen(true)}>
        {t('productsIssues.segmentBar.new')}
      </Button>

      <SegmentRuleBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onSave={handleBuilderSave}
        countFn={async () => 0}
        countRuleFn={countFor}
      />
    </div>
  )
}

IssuesSegmentBar.displayName = 'IssuesSegmentBar'
